import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Lucide from "@/components/Base/Lucide";
import { FormInput, FormCheck, FormSelect } from "@/components/Base/Form";
import Button from "@/components/Base/Button";
import Table from "@/components/Base/Table";
import Pagination from "@/components/Base/Pagination";
import Alert from "@/components/Base/Alert";

type Product = {
    id: string;
    name: string;
    sku?: string;
    barcode?: string;
    group?: string;
    type?: string;
    retailSellingPrice?: number;
    status: boolean;
};

type TeamProduct = {
    id: string;
    teamId: string;
    productId: string;
    status: boolean;
    createdAt: string;
    updatedAt: string;
};

export default function AssignProduct() {
    const { id: teamId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // State management
    const [products, setProducts] = useState<Product[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
    const [existingProducts, setExistingProducts] = useState<Set<string>>(new Set());
    const [originalProducts, setOriginalProducts] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [assigning, setAssigning] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [sortField, setSortField] = useState<keyof Product | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    // Get companyId from URL params
    const companyId = searchParams.get("companyId");

    // Load products and existing team products
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);

                // Fetch ALL products by paginating through all pages
                let allProducts: any[] = [];
                let currentPage = 1;
                let hasMorePages = true;
                const pageSize = 1000; // Fetch 1000 at a time

                while (hasMorePages) {
                    const queryParams = new URLSearchParams();
                    if (companyId) queryParams.append("companyId", companyId);
                    queryParams.append("pageSize", pageSize.toString());
                    queryParams.append("page", currentPage.toString());

                    const productsRes = await fetch(
                        `${import.meta.env.VITE_API_URL}/products?${queryParams.toString()}`,
                        {
                            headers: { Authorization: `Bearer ${getToken()}` }
                        }
                    );
                    const productsData = await productsRes.json();
                    const pageProducts = Array.isArray(productsData?.data) ? productsData.data : [];
                    
                    if (pageProducts.length > 0) {
                        allProducts = [...allProducts, ...pageProducts];
                        const totalPages = productsData.totalPages || 1;
                        hasMorePages = currentPage < totalPages;
                        currentPage++;
                    } else {
                        hasMorePages = false;
                    }
                }

                setProducts(allProducts);

                // Load existing team products
                const teamProductsRes = await fetch(
                    `${import.meta.env.VITE_API_URL}/teams/${teamId}/products`,
                    {
                        headers: { Authorization: `Bearer ${getToken()}` }
                    }
                );
                const teamProductsData = await teamProductsRes.json();
                const productIds: Set<string> = new Set(teamProductsData?.data?.map((teamProduct: TeamProduct) => teamProduct.productId) || []);
                setExistingProducts(productIds);
                setOriginalProducts(productIds);
                // Initialize selectedProducts with existing products so they appear checked
                setSelectedProducts(productIds);

            } catch (error) {
                console.error("Failed to load data:", error);
                setMessage({ type: 'error', text: 'Failed to load data' });
            } finally {
                setLoading(false);
            }
        };

        if (teamId) {
            loadData();
        }
    }, [teamId, companyId]);

    // Handle individual product selection
    const handleProductSelect = (productId: string) => {
        setSelectedProducts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(productId)) {
                newSet.delete(productId);
            } else {
                newSet.add(productId);
            }
            return newSet;
        });
    };

    // Handle select all
    const handleSelectAll = () => {
        const allProductIds = new Set(products.map(product => product.id));
        setSelectedProducts(allProductIds);
    };

    // Handle deselect all
    const handleDeselectAll = () => {
        setSelectedProducts(new Set());
    };

    // Handle column sorting
    const handleSort = (field: keyof Product) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Assign/remove selected products to/from team
    const handleAssignProducts = async () => {
        // Check if there are any changes to make
        const productsToAdd = Array.from(selectedProducts).filter(productId => !originalProducts.has(productId));
        const productsToRemove = Array.from(originalProducts).filter(productId => !selectedProducts.has(productId));

        if (productsToAdd.length === 0 && productsToRemove.length === 0) {
            setMessage({ type: 'error', text: 'No changes to make' });
            return;
        }

        try {
            setAssigning(true);
            setMessage(null);

            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            const promises = [];

            // Add new team products
            if (productsToAdd.length > 0) {
                const addPromises = productsToAdd.map(productId =>
                    fetch(`${apiUrl}/teams/${teamId}/products`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ productId, status: true })
                    })
                );
                promises.push(...addPromises);
            }

            // Remove team products
            if (productsToRemove.length > 0) {
                const removePromises = productsToRemove.map(productId =>
                    fetch(`${apiUrl}/teams/${teamId}/products/${productId}`, {
                        method: 'DELETE',
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    })
                );
                promises.push(...removePromises);
            }

            await Promise.all(promises);

            const actionText = productsToAdd.length > 0 && productsToRemove.length > 0
                ? `Successfully updated team: added ${productsToAdd.length} product(s), removed ${productsToRemove.length} product(s)`
                : productsToAdd.length > 0
                    ? `Successfully added ${productsToAdd.length} product(s) to the team`
                    : `Successfully removed ${productsToRemove.length} product(s) from the team`;

            setMessage({ type: 'success', text: actionText });
            setRefreshing(true);

            // Refresh existing products
            const teamProductsRes = await fetch(
                `${apiUrl}/teams/${teamId}/products`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const teamProductsData = await teamProductsRes.json();
            const productIds: Set<string> = new Set(teamProductsData?.data?.map((teamProduct: TeamProduct) => teamProduct.productId) || []);
            setExistingProducts(productIds);
            setOriginalProducts(productIds);
            // Update selectedProducts to reflect the new state (all current products should be checked)
            setSelectedProducts(productIds);
            setRefreshing(false);

        } catch (error) {
            console.error("Failed to update team products:", error);
            setMessage({ type: 'error', text: 'Failed to update team products' });
        } finally {
            setAssigning(false);
        }
    };

    // Filter and sort products
    const filteredAndSortedProducts = products
        .filter(product =>
            product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            product.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            product.barcode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            product.group?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            product.type?.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => {
            if (!sortField) return 0;

            const aValue = a[sortField];
            const bValue = b[sortField];

            const aStr = String(aValue || '');
            const bStr = String(bValue || '');

            if (sortDirection === 'asc') {
                return aStr.localeCompare(bStr);
            } else {
                return bStr.localeCompare(aStr);
            }
        });

    // Calculate pagination
    const totalFiltered = filteredAndSortedProducts.length;
    const calculatedTotalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedProducts = filteredAndSortedProducts.slice(startIndex, endIndex);

    // Update totalPages and total when filtered results change
    React.useEffect(() => {
        setTotalPages(calculatedTotalPages);
        setTotal(totalFiltered);
        // Reset to page 1 if current page is beyond available pages
        if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
            setCurrentPage(1);
        }
    }, [totalFiltered, pageSize, calculatedTotalPages]);

    // Pagination handlers
    const handleFirstPage = () => setCurrentPage(1);
    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };
    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };
    const handleLastPage = () => setCurrentPage(totalPages);
    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
                    <div>Loading products...</div>
                </div>
            </div>
        );
    }

    if (refreshing) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
                    <div>Updating team products...</div>
                </div>
            </div>
        );
    }

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <div className="grid grid-cols-12 gap-y-10 gap-x-6 max-w-7xl mx-auto">
                    <div className="col-span-12">
                        {/* Header */}
                        <div className="flex flex-col mt-4 md:mt-0 md:h-10 gap-y-3 md:items-center md:flex-row">
                            <div className="text-base font-medium group-[.mode--light]:text-white">
                                Assign Products to Team
                            </div>
                            <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleSelectAll}
                                    disabled={assigning}
                                >
                                    <Lucide icon="CheckSquare" className="stroke-[1.3] w-4 h-4 mr-2" />
                                    Select All
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleDeselectAll}
                                    disabled={assigning}
                                >
                                    <Lucide icon="Square" className="stroke-[1.3] w-4 h-4 mr-2" />
                                    Deselect All
                                </Button>
                            </div>
                        </div>

                        {/* Search and Table */}
                        <div className="mt-3.5">
                            <div className="flex flex-col box box--stacked max-w-full overflow-hidden">
                                <div className="flex flex-col p-5 sm:items-center sm:flex-row gap-y-2">
                                    <div className="flex flex-col sm:flex-row gap-y-2 sm:gap-x-4 sm:items-center w-full">
                                        <div className="relative">
                                            <Lucide
                                                icon="Search"
                                                className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                                            />
                                            <FormInput
                                                type="text"
                                                placeholder="Search products..."
                                                className="pl-9 sm:w-64 rounded-[0.5rem]"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                        </div>
                                        {message && (
                                            <div className={`text-sm px-3 py-2 rounded-md flex items-center sm:ml-auto ${message.type === 'success'
                                                    ? 'bg-success/10 text-success border border-success/20'
                                                    : 'bg-danger/10 text-danger border border-danger/20'
                                                }`}>
                                                <Lucide
                                                    icon={message.type === 'success' ? 'CheckCircle' : 'AlertTriangle'}
                                                    className="w-4 h-4 mr-2"
                                                />
                                                {message.text}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="overflow-auto xl:overflow-visible">
                                    <Table className="border-b border-slate-200/60 text-xs">
                                        <Table.Thead className="text-xs">
                                            <Table.Tr>
                                                <Table.Td
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('name')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Product Name
                                                        {sortField === 'name' && (
                                                            <Lucide
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                                                className="w-3 h-3"
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('sku')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        SKU
                                                        {sortField === 'sku' && (
                                                            <Lucide
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                                                className="w-3 h-3"
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('barcode')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Barcode
                                                        {sortField === 'barcode' && (
                                                            <Lucide
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                                                className="w-3 h-3"
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('group')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Group
                                                        {sortField === 'group' && (
                                                            <Lucide
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                                                className="w-3 h-3"
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('type')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Type
                                                        {sortField === 'type' && (
                                                            <Lucide
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                                                className="w-3 h-3"
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('retailSellingPrice')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Price
                                                        {sortField === 'retailSellingPrice' && (
                                                            <Lucide
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                                                className="w-3 h-3"
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('status')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Status
                                                        {sortField === 'status' && (
                                                            <Lucide
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                                                className="w-3 h-3"
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td className="text-center py-1.5">Assign</Table.Td>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {paginatedProducts.map((product) => (
                                                <Table.Tr key={product.id} className="[&_td]:last:border-b-0">
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {product.name}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {product.sku || "—"}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {product.barcode || "—"}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {product.group || "—"}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {product.type || "—"}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {product.retailSellingPrice ? `$${product.retailSellingPrice.toFixed(2)}` : "—"}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${product.status ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                                                            }`}>
                                                            {product.status ? "Active" : "Inactive"}
                                                        </span>
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 text-center border-dashed dark:bg-darkmode-600 text-xs">
                                                        <div className="flex justify-center items-center">
                                                            <FormCheck>
                                                                <FormCheck.Input
                                                                    type="checkbox"
                                                                    checked={selectedProducts.has(product.id)}
                                                                    onChange={() => handleProductSelect(product.id)}
                                                                    disabled={assigning}
                                                                    className="scale-90"
                                                                />
                                                            </FormCheck>
                                                        </div>
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-between items-center p-5 border-t border-slate-200/60">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => navigate(-1)}
                                        disabled={assigning}
                                    >
                                        <Lucide icon="ArrowLeft" className="w-4 h-4 mr-2" />
                                        Back
                                    </Button>
                                    <div className="flex gap-3">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => navigate(-1)}
                                            disabled={assigning}
                                        >
                                            <Lucide icon="X" className="w-4 h-4 mr-2" />
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleAssignProducts}
                                            disabled={assigning}
                                        >
                                            {assigning && <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />}
                                            <Lucide icon="Package" className="w-4 h-4 mr-2" />
                                            Update Team
                                        </Button>
                                    </div>
                                </div>

                                {/* Pagination */}
                                <div className="flex flex-col-reverse flex-wrap items-center p-5 flex-reverse gap-y-2 sm:flex-row">
                                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                        Showing {totalFiltered > 0 ? startIndex + 1 : 0} to {Math.min(endIndex, totalFiltered)} of {totalFiltered} products
                                    </div>
                                    <Pagination className="flex-1 w-full mr-auto sm:w-auto">
                                        <Pagination.Link
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleFirstPage();
                                            }}
                                            className={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronsLeft" className="w-4 h-4" />
                                        </Pagination.Link>
                                        <Pagination.Link
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handlePrevPage();
                                            }}
                                            className={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronLeft" className="w-4 h-4" />
                                        </Pagination.Link>
                                        {totalPages > 0 && (
                                            <>
                                                {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                                                    let pageNum: number;
                                                    if (totalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= totalPages - 2) {
                                                        pageNum = totalPages - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }
                                                    return (
                                                        <Pagination.Link
                                                            key={pageNum}
                                                            active={currentPage === pageNum}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setCurrentPage(pageNum);
                                                            }}
                                                            className="cursor-pointer"
                                                        >
                                                            {pageNum}
                                                        </Pagination.Link>
                                                    );
                                                })}
                                            </>
                                        )}
                                        <Pagination.Link
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleNextPage();
                                            }}
                                            className={currentPage >= totalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronRight" className="w-4 h-4" />
                                        </Pagination.Link>
                                        <Pagination.Link
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleLastPage();
                                            }}
                                            className={currentPage >= totalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronsRight" className="w-4 h-4" />
                                        </Pagination.Link>
                                    </Pagination>
                                    <FormSelect 
                                        className="sm:w-20 rounded-[0.5rem]"
                                        value={pageSize}
                                        onChange={handlePageSizeChange}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value={250}>250</option>
                                        <option value={500}>500</option>
                                    </FormSelect>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>
    );
}